import { isNumber } from 'lodash'
import PropTypes from 'prop-types'
import { createContext, useContext, useEffect, useMemo, useRef } from 'react'

import Paywall from '../components/paywall'
// Direkt aus den Dateien und nicht aus features/pricing/index.js. Der Index
// zieht hooks.js und damit use-talents-query.js, das wiederum context/index.js
// importiert. Über den Index entstünde ein Import-Zyklus.
import { usePricingAdminOverride } from '../features/pricing/admin-override'
import { usePricingEnforcementFlag } from '../features/pricing/enforcement-flag'
import { resolveEntitlements } from '../features/pricing/entitlements'
import { resolvePlanLimits } from '../features/pricing/limits'
import { planOf } from '../features/pricing/payment-selection'
import { usePaymentPlans } from '../features/pricing/plan-field'
import {
  LEGACY_FEATURE_KEYS,
  RESOURCES,
  UNLIMITED
} from '../features/pricing/plans'
import {
  isAccessExpired,
  isPeriodActive
} from '../features/pricing/space-access'
import { notifyBugsnag } from '../helpers'
import {
  buildPaymentAlertDescriptor,
  maybeSendPaymentAlert
} from '../helpers/payment-alert'
import { usePartnerContext } from './partner-context'
import { useUserContext } from './user-context'

const PaymentContext = createContext()

PaymentContext.displayName = 'PaymentContext'

// usePaymentRestrictions rechnet value >= restrictions[key]. Unbegrenzt wird
// UNLIMITED, also derselbe Wert, den Aivy OS speichert. So zeigen
// Mitgliederliste und Plan & Billing eine Zahl, und n >= 999999 ist false.
// Unbekannt (custom ohne Wert) wird undefined, denn n >= undefined ist false,
// n >= null dagegen true.
const toRestrictionCount = (limit) => {
  if (typeof limit !== 'number' || Number.isNaN(limit)) return undefined
  return Number.isFinite(limit) ? limit : UNLIMITED
}

// Legacy-Payments und Trials behalten ihre Rohwerte wie bisher, auch die
// Eigenheit, dass seat_count null Einladungen sperrt. Für ein Plan-Payment
// (echt oder simuliert) gelten die aufgelösten Limits.
const restrictionCountsOf = (payment, planById) => {
  const planId = planOf(payment, planById)

  if (!planId) {
    const { seat_count, career_count, spaces_count } = payment
    return { seat_count, career_count, spaces_count }
  }

  const limits = resolvePlanLimits({ planId, payment })

  return Object.fromEntries(
    Object.entries(RESOURCES).map(([resource, { field }]) => [
      field,
      toRestrictionCount(limits[resource].limit)
    ])
  )
}

// Die fünf Bestands-Booleans (attract, redirectTalentUrl, crossComparison,
// integrations, automations). In legacy und preview rechnet
// resolveEntitlements sie exakt wie bisher hier, in enforced vom Plan-Payment.
const legacyFeatureFlagsOf = (features) =>
  Object.fromEntries(
    Object.entries(LEGACY_FEATURE_KEYS).map(([key, name]) => [
      name,
      !!features[key]?.enabled
    ])
  )

// Unbekannter Plan-String oder Plan an einem Trial. Beides gilt als legacy,
// gemeldet wird je Payment und Grund einmal pro Seitenaufruf.
const reportedIgnoredPlans = new Set()

const reportIgnoredPlanPayments = (ignored) => {
  for (const { payment, reason } of ignored || []) {
    const key = `${payment?.id}:${reason}`
    if (reportedIgnoredPlans.has(key)) continue
    reportedIgnoredPlans.add(key)
    notifyBugsnag(
      new Error(`pricing plan ignoriert: ${reason} (payment ${payment?.id})`)
    )
  }
}

export function PaymentContextProvider({ payments, children }) {
  const paywallRef = useRef()

  const { isAdmin } = useUserContext()

  const { partner } = usePartnerContext()
  const partnerId = partner?.id

  // Plan-Feld, Schalter und Admin-Override. Ohne Plan-Payment bleibt der
  // Modus legacy, und der Wert ist derselbe wie vor dem Preismodell.
  const paymentPlans = usePaymentPlans({ partnerId, payments })
  const enforcementFlag = usePricingEnforcementFlag(partnerId)
  const { override: adminOverride } = usePricingAdminOverride({
    isAdmin,
    partnerId
  })

  // Der useMemo bleibt seiteneffektfrei und liefert zwei Dinge: den
  // Context-Wert und einen Alert-Deskriptor. Verschickt wird erst im
  // useEffect – vorher lief sendSlackMessage in der Render-Phase und feuerte
  // bei jedem Remount und jedem Dep-Wechsel neu.
  const { value, alert } = useMemo(() => {
    const showPaywall = (paywall) => {
      paywallRef.current.openPaywall({ paywall, type: 'modal' })
    }

    const entitlements = resolveEntitlements({
      payments,
      planById: paymentPlans.data,
      planFieldSettled: paymentPlans.settled,
      flagOn: enforcementFlag.on,
      flagsLoaded: enforcementFlag.loaded,
      isAdmin,
      adminOverride
    })

    // periodPayment ist das bisherige newestPayment, also das neueste
    // Payment ohne unused_campaign_credits. Es steuert weiter accessExpired
    // und den Payment-Alert. Nach einer Migration ist das das Plan-Payment,
    // das alte Payment alarmiert dann nicht mehr.
    const { periodPayment } = entitlements

    const ACCESS_EXPIRED = { accessExpired: true, entitlements }

    if (!periodPayment) return { value: ACCESS_EXPIRED, alert: null }

    const now = Date.now()

    // Zugriffs-Entscheidung – unveraendert. Die Klassifikation in
    // helpers/payment-alert.js steuert ausschliesslich den Alert und darf
    // niemandem den Zugang nehmen: ein Payment mit kaputtem Datum ist ein
    // Datenfehler, kein Kuendigungsgrund. Die Regel selbst steht in
    // features/pricing/space-access.js, damit der Standortwechsel andere
    // Standorte mit derselben Regel bewertet.
    const periodActive = isPeriodActive(periodPayment, now)

    // isAdmin = Aivy-Mitarbeitende (siehe user-context.js). Wenn wir selbst im
    // Kunden-Dashboard sind, ist der Alert kein Signal.
    const alert =
      isAdmin || periodActive
        ? null
        : buildPaymentAlertDescriptor({ payment: periodPayment, partner, now })

    // Trials laufen wirklich aus: hier endet der Zugang.
    if (isAccessExpired(periodPayment, now)) {
      return { value: ACCESS_EXPIRED, alert }
    }

    // Bewusst KEIN accessExpired bei einem abgelaufenen Kunden-Payment:
    // Zahlungen laufen regelmaessig aus, bevor die Verlaengerung im Aivy OS
    // hinterlegt ist. Wuerden wir hier sperren, waere ein interner
    // Prozess-Rueckstand ein Kunden-Outage. Der Slack-Alert IST die
    // Eskalation, der Zugang bleibt offen.

    const unusedCampaignCredits = payments
      .filter(({ unused_campaign_credits }) =>
        isNumber(unused_campaign_credits)
      )
      .map(({ unused_campaign_credits }) => unused_campaign_credits)
      .reduce((previousValue, currentValue) => previousValue + currentValue, 0)

    // Rechte, Produkte und Mengen kommen vom Entitlement-Payment. Das ist in
    // legacy und preview das legacyModePayment, in enforced das Plan-Payment.
    // Ohne Plan-Payment ist es dasselbe Objekt wie periodPayment.
    const payment = entitlements.payment || periodPayment

    // Die Laufzeit kommt immer vom periodPayment. Ist es dasselbe Payment,
    // bleibt der Spread unverändert, fehlende Felder fehlen also wie bisher.
    const period =
      payment === periodPayment
        ? {}
        : {
            start_at: periodPayment.start_at,
            expires_at: periodPayment.expires_at
          }

    // possible products
    // ATTRACT
    // SMARTPREDICT
    // FAKINGMODULE
    // INTERVIEWPAPER
    // BROWSERPLUGIN
    // REDIRECTTALENTURL
    // CROSSCOMPARISON
    // INTEGRATIONS
    // AUTOMATIONS

    // the dashboard uses the following products
    // ATTRACT
    // REDIRECTTALENTURL
    // CROSSCOMPARISON
    // INTEGRATIONS
    // AUTOMATIONS

    // the dashboard uses the following payment restrictions
    // seat_count, career_count, spaces_count (restrictionCountsOf), die
    // Produkt-Booleans kommen aus entitlements.features (LEGACY_FEATURE_KEYS)

    return {
      alert,
      value: {
        ...payment,
        ...period,
        accessExpired: false,
        is_trial: !!periodPayment.is_trial,
        ...restrictionCountsOf(payment, paymentPlans.data),
        ...legacyFeatureFlagsOf(entitlements.features),
        showPaywall,
        unusedCampaignCredits,
        entitlements
      }
    }
  }, [
    payments,
    isAdmin,
    partner,
    paymentPlans.data,
    paymentPlans.settled,
    enforcementFlag.on,
    enforcementFlag.loaded,
    adminOverride
  ])

  // `alert` ist bei jedem Memo-Rerun ein neues Objekt, der Effect kann also
  // mehrfach laufen. Die 24-h-Sperre in maybeSendPaymentAlert ist der
  // eigentliche Guard – deshalb steht sie dort und nicht in der Dep-Liste.
  useEffect(() => {
    maybeSendPaymentAlert(alert)
  }, [alert])

  const ignoredPlanPayments = value.entitlements?.ignoredPlanPayments

  useEffect(() => {
    reportIgnoredPlanPayments(ignoredPlanPayments)
  }, [ignoredPlanPayments])

  return (
    <>
      <Paywall ref={paywallRef} />
      <PaymentContext.Provider value={value}>
        {children}
      </PaymentContext.Provider>
    </>
  )
}

PaymentContextProvider.propTypes = {
  payments: PropTypes.array.isRequired,
  children: PropTypes.node.isRequired
}

export function usePaymentContext() {
  const context = useContext(PaymentContext)

  if (!context) {
    throw new Error('usePaymentContext must be used within a AppProvider')
  }

  return context
}

// Nur für Tests
export const resetPaymentContextForTests = () => {
  reportedIgnoredPlans.clear()
}
